import { Select } from '@myap/ui-library/cjs/components'
import {
  fetchApprovalRequests,
  clearSelectedApprovalRequest,
} from '../../../actions/approvalRequests'
import { Fade } from '../../common'
import { DetailPanelContainer } from '../../../containers/detailpanels'
import {
  selectApprovalRequestStatusProps,
  selectNumApprovalRequests,
  selectApprovalRequestType,
  selectSelectedApprovalRequestId,
  selectNumPendingApprovalRequests,
} from '../../../selectors/approvalRequests'
import { selectCurrentFilter } from '../../../selectors/filters'
import { ApprovalRequestsFilterControl, ApprovalRequestsTable } from '.'
import {
  APPROVAL_REQUEST_CONFIG,
  ARTYPE_NONSTOCKEDSSD,
  ARTYPE_OFFCYCLETESTING,
} from '../../../constants/ApprovalRequestConstants'
import tableStyles from '../../../assets/style/scss/table-utils.scss'
import enhanceStyles from '../../../assets/style/scss/enhancements.scss'
import {
  getCurrentEducationPeriodCd,
  getEducationPeriods,
  getPreviousEducationPeriodCd,
} from '../../../selectors/settings'
import { isEmpty } from '../../../utils/common'
import { updateApprovalTabCounts } from '../../../actions/tabs'

const EpcDropdown = ({ availableEpcs, currentEpc, previousEpc, selectedEpc, setSelectedEpc }) => (
  <div style={{ width: '200px', marginTop: '20px' }}>
    <Select
      name="Education Period Dropdown"
      label="Academic Year"
      options={[
        { value: currentEpc, label: availableEpcs[currentEpc]?.descr },
        { value: previousEpc, label: availableEpcs[previousEpc]?.descr },
      ]}
      emptyLabel={null}
      onChange={e => setSelectedEpc(parseInt(e.target.value, 10))}
      value={selectedEpc}
    />
  </div>
)

const mapStateToProps = (state, ownProps) => {
  const { arType } = ownProps
  const { fetching, error } = selectApprovalRequestStatusProps(state)
  const { filter } = selectCurrentFilter(state)
  const numApprovalRequests = selectNumApprovalRequests(state)
  const currentARType = selectApprovalRequestType(state)
  const currentEpc = getCurrentEducationPeriodCd(state)
  const previousEpc = getPreviousEducationPeriodCd(state)
  const selectedApprovalRequestId = selectSelectedApprovalRequestId(state)
  const availableEpcs = getEducationPeriods(state)
  const currentTablIsOffCycleOrNsssd =
    arType === ARTYPE_NONSTOCKEDSSD || arType === ARTYPE_OFFCYCLETESTING

  return {
    fetching,
    error,
    hasRequests: numApprovalRequests > 0,
    numberOfPendingRequests: selectNumPendingApprovalRequests(state),
    filter,
    arType,
    isMyARType: currentARType === arType,
    currentEpc,
    previousEpc,
    availableEpcs,
    showEpcDropdown: currentTablIsOffCycleOrNsssd,
    selectedApprovalRequestId,
  }
}

const ApprovalRequestsTab = ({
  title,
  hasRequests,
  filter,
  arType,
  isMyARType,
  tableSummary,
  headerColumns = [],
  approvalRequestColumns = [],
  RequestComponent,
  requestDetailPanels = [],
  fetchApprovalRequests,
  updateApprovalTabCounts,
  clearSelectedApprovalRequest,
  showEpcDropdown,
  selectedApprovalRequestId,
  availableEpcs,
  currentEpc,
  previousEpc,
  numberOfPendingRequests,
}) => {
  const [selectedEpc, setSelectedEpc] = useState(currentEpc)

  useEffect(() => {
    if (isMyARType) {
      updateApprovalTabCounts(arType, numberOfPendingRequests)
    }
  }, [isMyARType, numberOfPendingRequests])

  useEffect(() => {
    fetchApprovalRequests(arType, selectedEpc)
    return () => clearSelectedApprovalRequest()
  }, [arType, selectedEpc])

  if (!isMyARType) {
    return null
  }

  if (!hasRequests) {
    return (
      <>
        {showEpcDropdown ? (
          <EpcDropdown
            availableEpcs={availableEpcs}
            currentEpc={currentEpc}
            previousEpc={previousEpc}
            selectedEpc={selectedEpc}
            setSelectedEpc={setSelectedEpc}
          />
        ) : null}
        <p>No approval requests</p>
      </>
    )
  }

  return (
    <>
      <div className="row">
        <div className="col-md-4 col-sm-12">
          <h2 style={{ paddingTop: '13px' }}>{title}</h2>
        </div>
        <div className="col-md-8 col-sm-12">
          <ApprovalRequestsFilterControl />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <div style={{ borderBottom: '1px solid #d9d9d9' }} />
        </div>
      </div>
      <a
        id="skip-approval-request-table"
        className={tableStyles['skip-table-link']}
        href="#after-approval-requests-table"
      >
        Skip Approval Requests Table
      </a>
      {showEpcDropdown ? (
        <EpcDropdown
          availableEpcs={availableEpcs}
          currentEpc={currentEpc}
          previousEpc={previousEpc}
          selectedEpc={selectedEpc}
          setSelectedEpc={setSelectedEpc}
        />
      ) : null}
      <div className={`row ${enhanceStyles['grid--borderBetween']}`}>
        <ApprovalRequestsTable
          headerColumns={headerColumns}
          idField={APPROVAL_REQUEST_CONFIG[arType].idField}
          summary={tableSummary}
          caption={`A consolidated view of ${filter} approval requests`}
          approvalRequestColumns={approvalRequestColumns}
        />
        {!isEmpty(selectedApprovalRequestId) ? (
          <DetailPanelContainer
            requestComponent={RequestComponent}
            detailPanels={requestDetailPanels}
          />
        ) : null}
      </div>
      <span id="after-approval-requests-table" className="sr-only" />
    </>
  )
}

export default connect(mapStateToProps, {
  fetchApprovalRequests,
  updateApprovalTabCounts,
  clearSelectedApprovalRequest,
})(ApprovalRequestsTab)
